import { useEffect, useState } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import {
  dashboardStatusQueryOptions,
  MAX_POLL_DURATION_MS,
} from "@/lib/queries/dashboard.queries";

/** Summary/chart loader result shape — pending (202 + correlationId) or ready. */
type AsyncResourceSummary =
  | { status: "pending"; correlationId: string }
  | { status: "ready" }
  | null
  | undefined;

interface UseDashboardAsyncResourceArgs {
  /** Result of the summary/chart query for this resource. */
  summary: AsyncResourceSummary;
  /** Cache key this resource reads from — completed payloads are written here. */
  targetKey: QueryKey;
  /** Field carrying the payload on both the ready summary and the COMPLETED status. */
  resultField: "kpiCards" | "charts" | "section";
  /** Resets the poll when it changes (filters / mode). */
  resetKey: string;
  /** Whether this resource is active (e.g. the current dashboard mode). */
  enabled: boolean;
}

interface UseDashboardAsyncResourceResult<P> {
  data: P | null | undefined;
  status: string | undefined;
  timedOut: boolean;
}

/**
 * Drives one async dashboard resource: fire-and-poll a single BQ job, then write
 * the completed payload into its own query cache key. Each instance owns an
 * independent correlationId/poll, so cards and chart (or my-brand and competitors)
 * never contaminate each other.
 */
export function useDashboardAsyncResource<P>({
  summary,
  targetKey,
  resultField,
  resetKey,
  enabled,
}: UseDashboardAsyncResourceArgs): UseDashboardAsyncResourceResult<P> {
  const queryClient = useQueryClient();
  const ready = summary?.status === "ready";

  // Poll context is captured at start so a filter/mode change mid-poll can't land
  // a stale payload under the wrong cache key.
  const [poll, setPoll] = useState<{ correlationId: string; targetKey: QueryKey } | null>(null);
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setPoll(null);
    setPollStartedAt(0);
    setTimedOut(false);
  }, [resetKey]);

  // Start polling on the first 202 — never restart once captured (guarded by `poll`),
  // so a settled-in-cache summary doesn't re-fire the job and wipe the result.
  useEffect(() => {
    if (ready || !enabled || poll) return;
    if (summary?.status === "pending") {
      setPoll({ correlationId: summary.correlationId, targetKey });
      setPollStartedAt(Date.now());
      setTimedOut(false);
    }
  }, [summary, ready, enabled, poll, targetKey]);

  useEffect(() => {
    if (!pollStartedAt || ready) return;
    const remaining = MAX_POLL_DURATION_MS - (Date.now() - pollStartedAt);
    if (remaining <= 0) {
      setTimedOut(true);
      return;
    }
    const id = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(id);
  }, [pollStartedAt, ready]);

  const correlationId = poll?.correlationId ?? null;

  const { data: statusData } = useQuery({
    ...dashboardStatusQueryOptions(correlationId, pollStartedAt),
    enabled: !!correlationId && pollStartedAt > 0 && !ready,
  });

  // Persist the polled result into the resource cache so it survives status TTL
  // expiry (5 min) and we can stop polling immediately.
  useEffect(() => {
    if (statusData?.status !== "COMPLETED" || !poll) return;

    const payload = (statusData as unknown as Record<string, unknown>)[resultField] ?? null;
    queryClient.setQueryData(poll.targetKey, { status: "ready", [resultField]: payload });

    setPoll(null);
    setPollStartedAt(0);
    setTimedOut(false);
  }, [statusData, poll, queryClient, resultField]);

  const data =
    summary?.status === "ready"
      ? ((summary as unknown as Record<string, unknown>)[resultField] as P | null)
      : statusData?.status === "COMPLETED" && poll
        ? ((statusData as unknown as Record<string, unknown>)[resultField] as P | null)
        : undefined;

  return { data, status: statusData?.status, timedOut };
}
